import { CSSTransition, SwitchTransition } from 'react-transition-group';
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { currentBaseUrl } from '../../helpers/urlConst';
import LogInLogOutBtn from '../../modules/authentication/components/login-logout-btn';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import InputWrapper from '../../commons/components/input-wrapper';
import { smartLookEvent } from '../../helpers/analyticsHelper';
import useApiStatus from '../../store/apiStatus/useApiStatus';
import usePlatform from '../../commons/hooks/use-platform';
import DataContext from '../../store/dataContext';
import { status } from '../../helpers/constants';
import UiContext from '../../store/uiContext';
import useReset from '../../hooks/useReset';
import { useEmailValidation } from '../../commons/hooks/use-email-validation';
import { initialErrorState } from '../../commons/utils/initial-error-state';
import { useClickOutside } from '../../commons/hooks/use-click-outside';

import './style.scss';

const UserAdd = ({ handleUserAdd, apiEmailError, apiEmailLoading }) => {
  const { isAuthenticationSupported } = usePlatform();
  const { isMobileDevice } = useWindowDimensions();
  const { apiStatus } = useApiStatus();
  const { handleReset } = useReset();
  const { t } = useTranslation();
  const { validate } = useEmailValidation();

  const { email, isAutofill, isLoggedIn, portalSettings, updateEmail, updateIsAutofill } = useContext(DataContext);
  const { allowUserAdd, setEditEmail, showUserAddControl } = useContext(UiContext);

  const [emailTerm, setEmailTerm] = useState(email);
  const [error, setError] = useState(initialErrorState);

  const userEmailRef = useRef(null);
  const nextBtnRef = useRef(null);

  const emailInput = userEmailRef?.current;

  const handleEmailChange = value => {
    emailInput?.classList.remove('has-error');
    setEmailTerm(value);
    updateEmail(value);
  };

  const handleSubmit = async () => {
    const { hasError, message } = await validate(emailTerm);

    if (!hasError) {
      handleUserAdd(emailTerm);
      smartLookEvent('user-clicked-next');
    } else {
      setError({ hasError, message });
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const renderButtonsOrText = () => {
    if (isLoggedIn || isMobileDevice) {
      return <LogInLogOutBtn />;
    }
    return null;
  };

  const renderSignInWrapper = type => {
    const authorized = type === 'logged-in';
    const guest = type === 'default';

    return (
      <div className="section-email-login">
        <InputWrapper
          classNameInput={classNames({
            'is-value': emailTerm,
            'has-error': error.hasError,
            unauthorized: !authorized && !email,
          })}
          classNameWrapper={classNames({
            authorized,
          })}
          disabled={!allowUserAdd || authorized}
          error={error.message}
          inputId="user-email"
          onChange={event => handleEmailChange(event.target.value)}
          onFocus={() => setError(initialErrorState)}
          onKeyDown={handleKeyDown}
          placeholder={`* ${t('user_add.email')}`}
          placeholderTop={guest ? t('user_add.email') : t('user_add.logged_in_as')}
          ref={userEmailRef}
          theme={portalSettings.theme}
          type="email"
          value={emailTerm}
        >
          <div className="default-input" style={{ color: '#707070' }}>
            {renderButtonsOrText()}
          </div>
        </InputWrapper>
        {guest && (
          <button
            className="anchor-button"
            onClick={handleSubmit}
            id="next-btn"
            ref={nextBtnRef}
            disabled={!allowUserAdd || apiStatus.isEmailReady === status.LOADING}
            type="button"
            style={{
              backgroundColor: portalSettings.theme.mainColor,
              borderColor: portalSettings.theme.mainColor,
              color: portalSettings.theme.textColor,
            }}
          >
            {!apiEmailLoading ? (
              t('user_add.next')
            ) : (
              <img alt="Loading..." src={`${currentBaseUrl()}loaders/spinner-white.png`} />
            )}
          </button>
        )}
      </div>
    );
  };

  const renderKnownEmail = () => (
    <div className="section-checked-out-as">
      <div className="section-checked-out-as__d-flex">
        <p className="email-title">{`${t('user_add.email_checkout')}: `}</p>
        <p className="email-address">{email}</p>
      </div>
      {isAutofill ? (
        <button onClick={handleReset} type="button" className="btn-reset">
          <div>
            <p>{t('user_add.reset')}</p>
            <img src={`${currentBaseUrl()}assets/refresh.svg`} alt="Refresh" />
          </div>
        </button>
      ) : (
        <button onClick={() => setEditEmail(true)} type="button" className="btn-reset">
          <div style={{ alignItems: 'center' }}>
            <p>{t('user_add.edit')}</p>
            <img src={`${currentBaseUrl()}assets/edit-pencil.svg`} alt="Refresh" width={9} height={9} />
          </div>
        </button>
      )}
    </div>
  );

  useEffect(() => {
    const contextEmail = email;
    setEmailTerm(contextEmail);
    updateIsAutofill(false);
    setTimeout(() => {
      if (showUserAddControl) {
        userEmailRef?.current?.focus();
      }
    }, 200);
  }, [email]);

  useClickOutside(nextBtnRef);

  useEffect(() => {
    const bindFocusOnNextButton = () => {
      nextBtnRef.current.click();
    };
    if (nextBtnRef.current) {
      nextBtnRef.current.addEventListener('focus', bindFocusOnNextButton);
    }
  }, []);

  useEffect(() => {
    if (apiEmailError?.data?.success === false) {
      setError({ hasError: true, message: apiEmailError?.data?.errorMsg });
    }
  }, [apiEmailError]);

  return isLoggedIn && isAuthenticationSupported() ? (
    renderSignInWrapper('logged-in')
  ) : (
    <SwitchTransition mode="out-in">
      <CSSTransition
        addEndListener={node => {
          node.addEventListener('transitionend', () => null, false);
        }}
        timeout={250}
        classNames="animation"
        key={showUserAddControl}
      >
        {showUserAddControl ? renderSignInWrapper('default') : renderKnownEmail()}
      </CSSTransition>
    </SwitchTransition>
  );
};

UserAdd.propTypes = {
  handleUserAdd: PropTypes.func.isRequired,
  apiEmailError: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  apiEmailLoading: PropTypes.bool.isRequired,
};

export default UserAdd;
